import { can, formatDate } from '@clim/client-core';
import { PICKUP_STATUS_LABEL, SUPPLIER, pickupShareText, uuidv7, type DossierRecord, type PickupOrderRecord } from '@clim/domain';
import { useCallback, useEffect, useState } from 'react';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from './ui';

const day = (iso: string) => iso.split('-').reverse().join('/');
export const pickupTone = (o: PickupOrderRecord) =>
  o.status === 'picked_up' ? 'tone-success' : o.status === 'cancelled' ? 'tone-danger' : o.expired ? 'tone-warning' : 'tone-info';
export const pickupStatusText = (o: PickupOrderRecord) => (o.status === 'issued' && o.expired ? 'Expiré' : PICKUP_STATUS_LABEL[o.status]);

/**
 * Bons d'enlèvement chez le fournisseur d'un dossier : émis par le planificateur, consultés et téléchargés par le
 * technicien. Le contenu est figé ; le fournisseur vérifie le bon (QR code ou numéro + code) avant de remettre le
 * matériel, et le bon ne sert qu'une fois.
 */
export function DossierPickupOrders({ dossier }: { dossier: DossierRecord }) {
  const { me, api } = useReady();
  const [items, setItems] = useState<PickupOrderRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const planner = can(me, 'planning.manage');
  const load = useCallback(() => {
    api
      .request<{ items: PickupOrderRecord[] }>('GET', `/v1/dossiers/${dossier.id}/pickup-orders`)
      .then((r) => setItems(r.items), (e) => setError(errorMessage(e)));
  }, [api, dossier.id]);
  useEffect(load, [load]);
  if (!planner && !items?.length) return null;
  return (
    <div className="card" data-testid="pickup-orders">
      <div className="card-head">
        <h2>Bons d’enlèvement fournisseur</h2>
        {planner && dossier.status !== 'cancelled' && (
          <button className="btn small primary" onClick={() => setCreating(true)}>
            + Bon d’enlèvement
          </button>
        )}
      </div>
      {items === null ? <Empty>Chargement…</Empty> : !items.length ? <Empty>Aucun bon émis.</Empty> : null}
      {items?.map((o) => <PickupOrderCard key={o.id} o={o} planner={planner} onChange={load} />)}
      <ErrorBox message={error} />
      {creating && (
        <PickupOrderDialog
          dossier={dossier}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

export function PickupOrderCard({ o, planner, onChange }: { o: PickupOrderRecord; planner: boolean; onChange(): void }) {
  const { me, api } = useReady();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const open = (download: boolean) =>
    run(async () => {
      const r = await api.request<{ url: string }>('GET', `/v1/pickup-orders/${o.id}/pdf-url${download ? '?download=1' : ''}`);
      window.open(r.url, '_blank', 'noopener');
    });
  const live = o.status === 'issued';
  return (
    <div className="pickup-card" data-testid={`pickup-${o.number}`}>
      <div className="pickup-head">
        <strong>
          {o.number} — {o.supplierName}
        </strong>
        <span className={`badge ${pickupTone(o)}`}>{pickupStatusText(o)}</span>
      </div>
      <ul className="pickup-lines">
        {o.lines.map((l, k) => (
          <li key={k}>
            {l.quantity} × {l.label}
          </li>
        ))}
      </ul>
      <p className="muted small">
        {o.technicianName ? `Enlevé par ${o.technicianName}` : 'Technicien non désigné'} · émis le {formatDate(o.issuedAt)} par {o.issuedByName}
        {live ? ` · valable jusqu’au ${day(o.validUntil)}` : ''}
        {o.pickedUpAt ? ` · récupéré le ${formatDate(o.pickedUpAt)}` : ''}
        {o.cancelledAt ? ` · annulé le ${formatDate(o.cancelledAt)} (${o.cancelReason})` : ''}
      </p>
      {o.verifyPath && (
        <p className="small">
          Code de vérification : <strong className="mono">{o.verifyCode}</strong>
        </p>
      )}
      <div className="actions wrap">
        <button className="btn small" disabled={busy || !o.fileId} onClick={() => void open(false)}>
          Voir le bon (PDF)
        </button>
        <button className="btn small" disabled={busy || !o.fileId} onClick={() => void open(true)}>
          Télécharger
        </button>
        {live && (
          <a
            className="btn small"
            href={`https://wa.me/?text=${encodeURIComponent(pickupShareText(o, window.location.origin))}`}
            target="_blank"
            rel="noreferrer"
          >
            Envoyer au fournisseur (WhatsApp)
          </a>
        )}
        {live && (o.technicianId === me.user.id || planner) && (
          <button
            className="btn small primary"
            disabled={busy}
            onClick={() =>
              confirm('Confirmer que le matériel a été récupéré ? Le bon ne sera plus valable.') &&
              void run(() => api.request('POST', `/v1/pickup-orders/${o.id}/picked-up`))
            }
          >
            Matériel récupéré
          </button>
        )}
        {live && planner && (
          <button
            className="btn small danger"
            disabled={busy}
            onClick={() => {
              const reason = prompt('Motif de l’annulation (le fournisseur verra « Annulé ») :');
              if (reason?.trim()) void run(() => api.request('POST', `/v1/pickup-orders/${o.id}/cancel`, { reason }));
            }}
          >
            Annuler le bon
          </button>
        )}
      </div>
      <ErrorBox message={error} />
    </div>
  );
}

function PickupOrderDialog({ dossier, onClose, onDone }: { dossier: DossierRecord; onClose(): void; onDone(): void }) {
  const engine = useEngine();
  const { api } = useReady();
  const suppliers = engine
    .list('reason')
    .filter((r) => r.category === SUPPLIER && r.isActive)
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  // matériel prérempli : les articles de marque du dossier (climatiseurs, accessoires), modifiables
  const initial = () => {
    const all = engine
      .list('dossier_line')
      .filter((l) => l.dossierId === dossier.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const material = all.filter((l) => engine.get('catalog_item', l.itemId)?.brandId || l.model);
    return (material.length ? material : all).map((l) => ({ label: [l.label, l.model].filter(Boolean).join(' — '), quantity: String(l.quantity) }));
  };
  const [lines, setLines] = useState<{ label: string; quantity: string }[]>(() => {
    const l = initial();
    return l.length ? l : [{ label: '', quantity: '1' }];
  });
  const technicianOf = () =>
    engine
      .list('intervention')
      .filter((i) => i.dossierId === dossier.id && i.technicianId && !['cancelled', 'completed'].includes(i.status))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.technicianId ?? '';
  const [f, setF] = useState({ supplierId: suppliers[0]?.id ?? '', technicianId: technicianOf(), notes: '', validDays: '30' });
  const [techs, setTechs] = useState<{ id: string; name: string; company?: string | null }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    api
      .request<{ id: string; name: string }[]>('GET', `/v1/planning/technicians?territoryId=${dossier.territoryId}`)
      .then(setTechs, () => setTechs([]));
  }, [api, dossier.territoryId]);
  const setLine = (k: number, patch: Partial<{ label: string; quantity: string }>) =>
    setLines(lines.map((l, i) => (i === k ? { ...l, ...patch } : l)));
  const submit = async () => {
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      await api.request('POST', `/v1/dossiers/${dossier.id}/pickup-orders`, {
        id: uuidv7(),
        supplierId: f.supplierId,
        technicianId: f.technicianId || null,
        lines: lines.filter((l) => l.label.trim()).map((l) => ({ label: l.label, quantity: Number(l.quantity.replace(',', '.')) })),
        notes: f.notes || null,
        validDays: Number(f.validDays) || 30,
      });
      void engine.sync(); // le PDF apparaît dans les documents du dossier
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setErrors(fieldErrors(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Nouveau bon d’enlèvement" onClose={onClose}>
      <div className="form">
        <p className="muted small">
          Le bon est <strong>figé</strong> une fois émis (fournisseur, matériel, technicien), porte un numéro unique et ne sert qu’une
          fois. Jamais de prix sur le bon.
        </p>
        <Field label="Fournisseur" error={errors.supplierId} hint={suppliers.length ? undefined : 'Ajoutez vos fournisseurs dans Administration → Référentiels → Fournisseurs'}>
          <Select aria-label="Fournisseur" value={f.supplierId} onChange={(e) => setF({ ...f, supplierId: e.target.value })}>
            {!suppliers.length && <option value="">— aucun fournisseur —</option>}
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Technicien qui récupère le matériel" error={errors.technicianId}>
          <Select aria-label="Technicien qui récupère" value={f.technicianId} onChange={(e) => setF({ ...f, technicianId: e.target.value })}>
            <option value="">— à désigner —</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.company ? ` (${t.company})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <fieldset className="pickup-edit">
          <legend>Matériel à enlever (climatiseur, accessoires)</legend>
          {lines.map((l, k) => (
            <div className="pickup-line-edit" key={k}>
              <Input aria-label={`Article ${k + 1}`} value={l.label} placeholder="Désignation" onChange={(e) => setLine(k, { label: e.target.value })} style={{ flex: 1 }} />
              <Input
                aria-label={`Quantité ${k + 1}`}
                value={l.quantity}
                inputMode="decimal"
                onChange={(e) => setLine(k, { quantity: e.target.value })}
                style={{ width: '4.5rem' }}
              />
              <button className="btn small" aria-label={`Retirer l’article ${k + 1}`} onClick={() => setLines(lines.filter((_, i) => i !== k))}>
                ✕
              </button>
            </div>
          ))}
          <button className="btn small" onClick={() => setLines([...lines, { label: '', quantity: '1' }])}>
            + Ajouter un article
          </button>
          {errors.lines && <p className="field-error">{errors.lines}</p>}
        </fieldset>
        <Field label="Précisions pour le fournisseur (facultatif)">
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </Field>
        <Field label="Valable (jours)">
          <Input aria-label="Validité en jours" value={f.validDays} inputMode="numeric" onChange={(e) => setF({ ...f, validDays: e.target.value })} style={{ width: '6rem' }} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Fermer
          </button>
          <button className="btn primary" disabled={busy || !f.supplierId} onClick={() => void submit()}>
            Émettre le bon
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** « Mes bons d'enlèvement » (technicien) : tous ses chantiers, valables d'abord. Nécessite la connexion. */
export function MyPickupOrders() {
  const { me, api } = useReady();
  const [items, setItems] = useState<PickupOrderRecord[] | null>(null);
  const load = useCallback(() => {
    api.request<{ items: PickupOrderRecord[] }>('GET', '/v1/pickup-orders/mine').then(
      (r) => setItems(r.items),
      () => setItems([]),
    );
  }, [api]);
  useEffect(load, [load]);
  if (!items?.length) return null;
  const sorted = [...items].sort((a, b) => Number(b.status === 'issued') - Number(a.status === 'issued'));
  return (
    <div className="card" data-testid="my-pickup-orders">
      <div className="card-head">
        <h2>Mes bons d’enlèvement</h2>
      </div>
      {sorted.map((o) => (
        <div key={o.id}>
          <p className="small muted">
            Chantier : {o.clientLabel}
            {o.siteCity ? ` — ${o.siteCity}` : ''}
          </p>
          <PickupOrderCard o={o} planner={can(me, 'planning.manage')} onChange={load} />
        </div>
      ))}
    </div>
  );
}
